// src/components/CreateAccount.js

import React, { useEffect, useState } from "react";
import { auth, db } from "../firebase";
import {
  createUserWithEmailAndPassword,
  sendPasswordResetEmail,
} from "firebase/auth";
import {
  doc,
  setDoc,
  collection,
  getDocs,
  updateDoc,
  query,
  where,
} from "firebase/firestore";
import "../styles/createAccount.css";

function CreateAccount() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    role: "employee",
    vehicles: [],
    company: "",
  });

  const [showPassword, setShowPassword] = useState(false);
  const [users, setUsers] = useState([]);

  const vehicleOptions = ["DAF5019", "ABJ3284", "GW3509", "KT0666"];

  const companyOptions = [
    "Sarathchandra Textile",
    "Centauria Lake",
    "Centauria Wild",
  ];

  useEffect(() => {
    const fetchUsers = async () => {
      const q = query(collection(db, "users"), where("disabled", "!=", true));
      const snapshot = await getDocs(q);
      const userList = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setUsers(userList);
    };

    fetchUsers();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;

    // Reset vehicles & company when role changes
    if (name === "role") {
      const updatedData = { ...formData, role: value };

      if (value !== "driver") updatedData.vehicles = [];
      if (value !== "admin") updatedData.company = "";

      setFormData(updatedData);
      return;
    }

    setFormData({ ...formData, [name]: value });
  };

  const handleVehicleToggle = (vehicle) => {
    setFormData((prev) => {
      const vehicles = prev.vehicles.includes(vehicle)
        ? prev.vehicles.filter((v) => v !== vehicle)
        : [...prev.vehicles, vehicle];
      return { ...prev, vehicles };
    });
  };

  const handleCreateAccount = async (e) => {
    e.preventDefault();

    // Validate driver vehicle selection
    if (formData.role === "driver" && formData.vehicles.length === 0) {
      alert("Please select at least one vehicle type for the driver.");
      return;
    }

    // Validate company selection for admin
    if (formData.role === "admin" && !formData.company) {
      alert("Please select a company for the admin.");
      return;
    }

    try {
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        formData.email,
        formData.password
      );
      const user = userCredential.user;

      const userData = {
        uid: user.uid,
        name: formData.name,
        email: formData.email,
        role: formData.role,
        disabled: false,
      };

      if (formData.role === "driver") {
        userData.vehicles = formData.vehicles;
      }

      if (formData.role === "admin") {
        userData.company = formData.company;
      }

      await setDoc(doc(db, "users", user.uid), userData);

      alert(
        `${formData.role.charAt(0).toUpperCase() + formData.role.slice(1)} account created successfully!`
      );

      setFormData({
        name: "",
        email: "",
        password: "",
        role: "employee",
        vehicles: [],
        company: "",
      });

      const q = query(collection(db, "users"), where("disabled", "!=", true));
      const snapshot = await getDocs(q);
      const userList = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setUsers(userList);
    } catch (error) {
      alert(`Error: ${error.message}`);
    }
  };

  const handleRename = async (id, newName) => {
    await updateDoc(doc(db, "users", id), { name: newName });
    setUsers(
      users.map((user) => (user.id === id ? { ...user, name: newName } : user))
    );
  };

  const handleResetPassword = async (email) => {
    try {
      await sendPasswordResetEmail(auth, email);
      alert(`Password reset link sent to ${email}`);
    } catch (error) {
      alert(`Failed to send reset email: ${error.message}`);
    }
  };

  return (
    <div className="create-account-container">
      <h2>Create Account</h2>

      <form onSubmit={handleCreateAccount} className="create-account-form">
        <input
          type="text"
          name="name"
          placeholder="Name"
          value={formData.name}
          onChange={handleChange}
          required
        />

        <input
          type="email"
          name="email"
          placeholder="Email"
          value={formData.email}
          onChange={handleChange}
          autoComplete="off"
          required
        />

        <input
          type={showPassword ? "text" : "password"}
          name="password"
          placeholder="Password"
          value={formData.password}
          onChange={handleChange}
          autoComplete="new-password"
          required
        />

        <label className="show-password-toggle">
          <input
            type="checkbox"
            checked={showPassword}
            onChange={() => setShowPassword((prev) => !prev)}
          />
          Show Password
        </label>

        <select name="role" value={formData.role} onChange={handleChange}>
          <option value="employee">User</option>
          <option value="security">Security</option>
          <option value="admin">Admin</option>
          <option value="Superadmin">Super_Admin</option>
          <option value="driver">Driver</option>
        </select>

        {/* Admin – Company Selection */}
        {formData.role === "admin" && (
          <div className="company-selection">
            <label style={{ color: "#000" }}>Select Company:</label>
            <select
              name="company"
              value={formData.company}
              onChange={handleChange}
              required
            >
              <option value="">-- Select Company --</option>
              {companyOptions.map((company) => (
                <option key={company} value={company}>
                  {company}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Driver – Vehicle Selection */}
        {formData.role === "driver" && (
          <div className="vehicle-selection">
            <label style={{ color: "#000" }}>Select Vehicle Types:</label>
            <div className="vehicle-checkboxes">
              {vehicleOptions.map((vehicle) => (
                <label
                  key={vehicle}
                  className="vehicle-checkbox-label"
                  style={{ color: "#000" }}
                >
                  <input
                    type="checkbox"
                    checked={formData.vehicles.includes(vehicle)}
                    onChange={() => handleVehicleToggle(vehicle)}
                  />
                  <span style={{ color: "#000" }}>{vehicle}</span>
                </label>
              ))}
            </div>

            {formData.vehicles.length > 0 && (
              <div className="selected-vehicles">
                Selected: {formData.vehicles.join(", ")}
              </div>
            )}
          </div>
        )}

        <button type="submit">Create Account</button>
      </form>

      <h3>Manage Accounts</h3>

      <table className="account-table">
        <thead>
          <tr>
            <th>Name</th>
            <th>Email</th>
            <th>Role</th>
            <th>Vehicles</th>
            <th>Company</th>
            <th>Actions</th>
          </tr>
        </thead>

        <tbody>
          {users.map((user) => (
            <tr key={user.id}>
              <td data-label="Name">
                <input
                  type="text"
                  value={user.name}
                  onChange={(e) => handleRename(user.id, e.target.value)}
                />
              </td>

              <td data-label="Email">{user.email}</td>
              <td data-label="Role">{user.role}</td>

              <td data-label="Vehicles">
                {user.role === "driver" && user.vehicles
                  ? user.vehicles.join(", ")
                  : "-"}
              </td>

              <td data-label="Company">
                {user.role === "admin" ? user.company || "Not Assigned" : "-"}
              </td>

              <td data-label="Actions">
                <button onClick={() => handleResetPassword(user.email)}>
                  Reset Password
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default CreateAccount;
